import type { BankIDStatus, ReferenceName } from '@sysarb/sysarb-types/services/v1/login';
import type { LoginType } from '@sysarb/sysarb-types/services/v1/sso';
import { getApiHost } from '@/appSettings';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { UseMutationOptions, QueryOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { ValidateTokenResponse } from '@/login/types/AccessTokenData';

const accessTokenService = new RestManager(`https://${getApiHost()}/login-service/accesstoken`);
const loginService = new RestManager(`https://${getApiHost()}/login-service/login`);
const ssoService = new RestManager(`https://${getApiHost()}/sso-service`);

export type ConnectUserPayload = { token: string; username: string };
export type AuthPasswordPayload = { username: string; password: string };
export type AuthSmsCodePayload = { username: string; code: string };
export type BankIDResultPayload = { referenceName: string };
export type PasswordLoginResult = { requiresSmsCode: boolean } | null;
export type LoginSettingsByUsernamePayload = { username: string };
/** A single SSO setup the user can authenticate through. */
export type SsoOption = { id: string; name: string };
export type SsoSettingsByUsernameResult = {
    username: string;
    loginType: LoginType;
    sso: SsoOption[];
    password: string | null;
} | null;

type LoginOptions = {
    queries: {
        validate: (enabled?: boolean) => QueryOptions<null, Error>,
        invalidate: () => QueryOptions<string | null, Error>,
    },
    mutations: {
        loginSettingsByUsername: UseMutationOptions<SsoSettingsByUsernameResult, Error, LoginSettingsByUsernamePayload>,
        authPassword: UseMutationOptions<PasswordLoginResult, Error, AuthPasswordPayload>,
        authSmsCode: UseMutationOptions<boolean, Error, AuthSmsCodePayload>,
        startBankIDAuth: UseMutationOptions<ReferenceName | null, Error, void>,
        pollBankIDResult: UseMutationOptions<BankIDStatus | null, Error, BankIDResultPayload>,
        pollBankIDQrCode: UseMutationOptions<{ qrCode: string } | null, Error, BankIDResultPayload>,
        abortBankIDAuth: UseMutationOptions<void, Error, BankIDResultPayload>,
    },
};

export const login: LoginOptions = {
    queries: {
        validate: (enabled: boolean = false): QueryOptions<null, Error> => {
            const url = 'validate';
            return queryOptions<null, Error>({
                queryKey: ['validate', url],
                queryFn: async (): Promise<null> => {
                    const loginStore = useLoginStore();
                    try {
                        const result = await accessTokenService.get<ValidateTokenResponse | null>(url);

                        if (result == null) {
                            loginStore.setTokenUserInfo(null);
                            return null;
                        }

                        if (loginStore.tokenUserInfo == null) {
                            loginStore.setTokenUserInfo(result);
                            return null;
                        }

                        if (result.userId !== loginStore.tokenUserInfo.userId) {
                            loginStore.tokenUserInfo.userId = result.userId;
                        }

                        if (result.isSupport !== loginStore.tokenUserInfo.isSupport) {
                            loginStore.tokenUserInfo.isSupport = result.isSupport;
                        }

                        if (result.isSimulated !== loginStore.tokenUserInfo.isSimulated) {
                            loginStore.tokenUserInfo.isSimulated = result.isSimulated;
                        }
                    } catch {
                        loginStore.setTokenUserInfo(null);
                    }
                    return null;
                },
                enabled: () => enabled
            });
        },
        invalidate: (): QueryOptions<string | null, Error> => {
            const url = 'invalidate';
            return queryOptions<string | null, Error>({
                queryKey: ['invalidate', url],
                queryFn: async (): Promise<string | null> => {
                    try {
                        return await accessTokenService.get<string>(url);
                    } catch {
                        return null;
                    }
                }
            });
        }
    },
    mutations: {
        loginSettingsByUsername: {
            mutationFn: async (payload: LoginSettingsByUsernamePayload): Promise<SsoSettingsByUsernameResult> => {
                try {
                    return await ssoService.post<SsoSettingsByUsernameResult>(payload, 'v1/login/username');
                } catch (e: unknown) {
                    console.error('login.loginSettingsByUsername', e);
                    return null;
                }
            }
        },
        authPassword: {
            mutationFn: async (payload: AuthPasswordPayload): Promise<PasswordLoginResult> => {
                try {
                    const result = await loginService.post<PasswordLoginResult>(payload, 'login/password');
                    return result;
                } catch (e: unknown) {
                    console.error('login.authPassword', e);
                    return null;
                }
            }
        },
        authSmsCode: {
            mutationFn: async (payload: AuthSmsCodePayload): Promise<boolean> => {
                try {
                    await loginService.post(payload, 'login/smscode');
                    return true;
                } catch (e: unknown) {
                    console.error('login.authSmsCode', e);
                    return false;
                }
            }
        },
        startBankIDAuth: {
            mutationFn: async (): Promise<ReferenceName | null> => {
                try {
                    return await loginService.post<ReferenceName>({}, 'bankid/startbankidauth');
                } catch (e: unknown) {
                    console.error('login.startBankIDAuth', e);
                    return null;
                }
            }
        },
        pollBankIDResult: {
            mutationFn: async (payload: BankIDResultPayload): Promise<BankIDStatus | null> => {
                try {
                    return await loginService.post<BankIDStatus>({
                        referenceName: payload.referenceName,
                        handleThrow: true
                    }, 'bankid/pollbankidresult');
                } catch (e: unknown) {
                    console.error('login.pollBankIDResult', e);
                    return null;
                }
            }
        },
        pollBankIDQrCode: {
            mutationFn: async (payload: BankIDResultPayload): Promise<{ qrCode: string } | null> => {
                try {
                    return await loginService.post<{ qrCode: string }>({ referenceName: payload.referenceName }, 'bankid/getbankidqr');
                } catch (e: unknown) {
                    console.error('login.pollBankIDQrCode', e);
                    return null;
                }
            }
        },
        abortBankIDAuth: {
            mutationFn: async (payload: BankIDResultPayload): Promise<void> => {
                try {
                    await loginService.post({ referenceName: payload.referenceName }, 'bankid/abortbankidauth');
                } catch (e: unknown) {
                    console.error('login.abortBankIDAuth', e);
                }
            }
        },
    },
};
